/**
 * OrderHistoryTab Component
 * Displays the agent's order history with clickable rows to view order details.
 * Responsive: table on desktop, cards on mobile.
 */

import { useState, useEffect, useCallback, memo } from 'react';
import {
  ClipboardDocumentListIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { OathEmptyState, OathAlert } from '../../shared/OathUI';
import { getOrderHistory } from '../../../services/oathFormsService';
import OrderDetailModal from './OrderDetailModal';
import { StatusPill } from './formsConstants';

/**
 * OrderHistoryTab
 *
 * @param {number} agencyId - Agency ID to fetch orders for
 */
const OrderHistoryTab = ({ agencyId }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  const fetchOrders = useCallback(async () => {
    if (!agencyId) return;

    setLoading(true);
    setError(null);

    const result = await getOrderHistory(agencyId);

    if (result.success) {
      // Handle both response shapes: direct array or nested orderHistory
      const orderList = Array.isArray(result.data)
        ? result.data
        : result.data?.orderHistory || [];
      setOrders(orderList);
    } else {
      setError(result.error || 'Failed to load order history');
    }
    setLoading(false);
  }, [agencyId]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Loading skeleton
  if (loading) {
    return (
      <div className="p-6">
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="animate-pulse flex items-center gap-4 p-4 rounded-lg bg-gray-50 dark:bg-gray-700/30">
              <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-24" />
              <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-32" />
              <div className="flex-1" />
              <div className="h-5 bg-gray-200 dark:bg-gray-600 rounded-full w-20" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <OathAlert variant="error" message={error} />
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <OathEmptyState
        icon={ClipboardDocumentListIcon}
        title="No orders yet"
        message="When you place form orders, they will appear here."
      />
    );
  }

  return (
    <>
      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-700/30">
              <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide px-4 py-3">
                Date
              </th>
              <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide px-4 py-3">
                Ship To
              </th>
              <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide px-4 py-3 w-20">
                Order #
              </th>
              <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide px-4 py-3 w-28">
                Status
              </th>
              <th className="px-4 py-3 w-10" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
            {orders.map((order) => (
              <tr
                key={order.orderId}
                onClick={() => setSelectedOrderId(order.orderId)}
                className="hover:bg-gray-50/50 dark:hover:bg-gray-700/20 cursor-pointer transition-colors"
              >
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                  {order.date}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                  {order.agentName || '-'}
                </td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                  #{order.orderId}
                </td>
                <td className="px-4 py-3">
                  <StatusPill status={order.status} />
                </td>
                <td className="px-4 py-3">
                  <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden divide-y divide-gray-100 dark:divide-gray-700/50">
        {orders.map((order) => (
          <button
            key={order.orderId}
            type="button"
            onClick={() => setSelectedOrderId(order.orderId)}
            className="w-full text-left p-4 hover:bg-gray-50 dark:hover:bg-gray-700/20 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Order #{order.orderId}
                  </p>
                  <StatusPill status={order.status} />
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {order.date}
                  </p>
                  {order.agentName && (
                    <>
                      <span className="text-xs text-gray-300 dark:text-gray-600">|</span>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {order.agentName}
                      </p>
                    </>
                  )}
                </div>
              </div>
              <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
            </div>
          </button>
        ))}
      </div>

      {/* Order detail modal */}
      <OrderDetailModal
        isOpen={selectedOrderId !== null}
        onClose={() => setSelectedOrderId(null)}
        orderId={selectedOrderId}
      />
    </>
  );
};

export default memo(OrderHistoryTab);
